/**
 * Shared DTOs for platform sales-point contacts API (plan §3.2–3.4 / S9).
 * Consumed by account Shop machine + L3 platform client.
 *
 * Wire `{success,data}` may send nullish `tenantCode` / `tenantName` / `customerShopUrl`.
 * `SalesPointContactItemDTO` is the **post-normalize** client contract (see
 * `normalizeSalesPointContactItem` in salesPointContactsApi).
 */

import type { LocalizedNameMap, LogoChipBackgroundSettings, LogoChipRimSettings } from 'pi-kiosk-shared';
import type { SalesPointCustomerPresence } from '../shops/customerPresence.js';

/** Post-normalize row — tenantCode / tenantName / customerShopUrl are strings. */
export interface SalesPointContactItemDTO extends LogoChipRimSettings, LogoChipBackgroundSettings {
  salesPointId: number;
  code: string;
  name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  nameLocales?: LocalizedNameMap | null;
  location: string | null;
  /**
   * A13b — Shipping-only display from customerPresence (never treat ONLINE_ONLY null as locationMissing).
   * Wire may omit; normalize defaults STOREFRONT.
   */
  customerPresence: SalesPointCustomerPresence;
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  customerShopUrl: string;
  lastInteractionAt: string;
  isFavorite?: boolean;
  favoriteOptOut?: boolean;
  /** Signed SP image stream URL, or null when no SalesPointImage / storage off. */
  imageUrl: string | null;
  /** Optional dark-regime SP image; falls back to {@link imageUrl}. */
  imageUrlDark: string | null;
  /** Signed tenant square logo stream URL, or null when absent / applyToCustomerPwa off. */
  tenantLogoUrl: string | null;
  /** Optional dark-regime tenant square logo; falls back to {@link tenantLogoUrl}. */
  tenantLogoUrlDark: string | null;
}

/** Pre-normalize wire row — string tenant fields may be nullish / wrong-typed; rim optional. */
export type SalesPointContactItemWire = Omit<
  SalesPointContactItemDTO,
  | 'tenantCode'
  | 'tenantName'
  | 'customerShopUrl'
  | 'showLogoChipRimLight'
  | 'showLogoChipRimDark'
  | 'logoChipRimColorLight'
  | 'logoChipRimColorDark'
  | 'showLogoChipBackgroundLight'
  | 'showLogoChipBackgroundDark'
  | 'logoChipBackgroundColorLight'
  | 'logoChipBackgroundColorDark'
> & {
  tenantCode?: string | null;
  tenantName?: string | null;
  customerShopUrl?: string | null;
  showLogoChipRimLight?: boolean;
  showLogoChipRimDark?: boolean;
  logoChipRimColorLight?: string;
  logoChipRimColorDark?: string;
  showLogoChipBackgroundLight?: boolean;
  showLogoChipBackgroundDark?: boolean;
  logoChipBackgroundColorLight?: string;
  logoChipBackgroundColorDark?: string;
};

export interface SalesPointContactsListDTO {
  items: SalesPointContactItemDTO[];
  /** True when list should auto-open the sole contact (server flag ∧ post-normalize length === 1). */
  autoSelect: boolean;
}

/** POST /sales-point-contacts/record body — one of the two shapes. */
export type RecordSalesPointContactBody =
  | { salesPointId: number }
  | { tenantCode: string; salesPointSlug: string };

export interface PendingSpContactRef {
  tenantCode: string;
  salesPointId?: number;
  salesPointSlug?: string;
  seenAt: string;
}

export interface ClaimSalesPointContactsBody {
  contacts: PendingSpContactRef[];
}

export interface ClaimSalesPointContactsFailedItem {
  ref: PendingSpContactRef;
  code: string;
}

export interface ClaimSalesPointContactsResultDTO {
  activated: PendingSpContactRef[];
  failed: ClaimSalesPointContactsFailedItem[];
}

/** Error codes from record/claim — FE treats MEMBERSHIP_BLOCKED as silent. */
export const SP_CONTACT_ERROR_CODES = {
  SP_NOT_FOUND: 'SP_NOT_FOUND',
  SP_NOT_PRODUCTS: 'SP_NOT_PRODUCTS',
  SP_INACTIVE: 'SP_INACTIVE',
  MEMBERSHIP_BLOCKED: 'MEMBERSHIP_BLOCKED',
  RATE_LIMITED: 'RATE_LIMITED',
  CLAIM_CAP_EXCEEDED: 'CLAIM_CAP_EXCEEDED',
} as const;

export type SpContactErrorCode =
  (typeof SP_CONTACT_ERROR_CODES)[keyof typeof SP_CONTACT_ERROR_CODES];
